import { Alert, PageHeader, Skeleton } from '@hvac/ui';
import { type AuditEventRecord } from '@hvac/contracts';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { sourceOf, type Source } from './timeline-format.js';
import { TimelineList } from '../objects/object-panels.js';
import { organizationTimelineQuery } from '../../shared/api/object-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';

/**
 * ⌘T, everything that happened, one stream.
 *
 * The business has one timeline and every object exposes a slice of it. This
 * page is the whole thing: uploads, records, exports, learning, corrections,
 * in the audit trail's own words, newest first. It is a read of the trail,
 * there is nothing to manage here, only to know.
 */
export function TimelinePage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId;

  const timeline = useQuery({
    ...organizationTimelineQuery(organizationId ?? ''),
    enabled: organizationId !== null,
  });

  if (organizationId === null) return null;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Today"
        title="Timeline"
        description="Everything that happened: every change to the books, recorded by the database itself with who made it and what moved, and every sign-in. Each record's page shows its slice of this same stream."
      />

      {timeline.isPending ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-6 w-3/5" />
        </div>
      ) : timeline.isError ? (
        <Alert tone="danger" title="The timeline could not be read">
          {timeline.error.message}
        </Alert>
      ) : timeline.data.items.length === 0 ? (
        <p className="text-sm text-content-muted">
          Nothing yet. The first upload starts the story.
        </p>
      ) : (
        <FilteredTimeline events={timeline.data.items} />
      )}
    </div>
  );
}

const FILTERS: readonly { key: 'all' | Source; label: string }[] = [
  { key: 'all', label: 'Everything' },
  { key: 'person', label: 'People' },
  { key: 'automatic', label: 'Filed automatically' },
  { key: 'system', label: 'The system' },
];

/** The trail, narrowed to one kind of hand when that is the question. */
function FilteredTimeline({ events }: { events: AuditEventRecord[] }) {
  const [filter, setFilter] = useState<'all' | Source>('all');
  const shown = filter === 'all' ? events : events.filter((event) => sourceOf(event) === filter);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-1" role="group" aria-label="Whose changes">
        {FILTERS.map((option) => (
          <button
            key={option.key}
            type="button"
            aria-pressed={filter === option.key}
            onClick={() => setFilter(option.key)}
            className={[
              'rounded-[var(--radius-control)] px-3 py-1 text-sm',
              filter === option.key
                ? 'bg-brand-50 font-medium text-brand-700'
                : 'text-content-muted hover:bg-surface-sunken',
            ].join(' ')}
          >
            {option.label}
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <p className="text-sm text-content-muted">Nothing of that kind in the latest entries.</p>
      ) : (
        <TimelineList events={shown} />
      )}
    </div>
  );
}
